import callStack from './call-stack.png'
import callStackWhatHow from './call-stack-what-how.png'
import callStackLevelsOfAbstraction from './call-stack-levels-of-abstraction.png'
import snippetAbstractions from './snippet-abstractions.png'
import snippetAbstractionsFix from './snippet-abstraction-fix.png'

const content = `<p>The second fundamental concept of software design is <strong>respecting levels of abstraction</strong>.</p>

<p>Respecting levels of abstraction is essential for two reasons:</p>

<ul><li>it reduces coupling</li><li>it makes code expressive</li></ul>

<p>Those two effects contribute to making code easy to change, which is the purpose of software design.</p>

<p>First let’s see what levels of abstraction are, and then what <em>respecting</em> levels of abstractions is about.</p>

<h2 id="The-What-and-the-How">The What and the How</h2>

<p>Levels of abstractions are about <strong>What</strong> the code is doing, and <strong>How</strong> it’s doing it.</p>

<p>All this becomes more concrete with an example. Let’s imagine an e-commerce application where the user can buy products, and some products have a discount at the moment you select them.</p>

<p>To compute the total price of the current cart, the code loops over the products and computes amongst other things the discount for each product. The call stack may look like this:</p>

<img src=${callStack} alt="call-stack" />

<p>To compute the total price of the cart, the function <code>price_cart</code> calls <code>price_product</code> for each product in the cart. This prices the product, and in particular its potential discount. For this last step, <code>price_product</code> calls <code>apply_discount</code>.</p>

<p><code>apply_discount</code> uses a bit of maths to compute the discounted price, and calls the arithmetic operators offered by the programming language. Those arithmetic operators in turn call binary code that maps to instructions sent to the CPU.</p>

<p>In this example, each level of the stack corresponds to <strong>What</strong> the code is doing: it’s computing the price of the cart, it’s computing the price of a product, it’s applying a discount, etc.</p>

<p>And the relation between one level and the next is <strong>How</strong>:</p>

<ul><li><strong>How</strong> to compute a cart’s price? By computing the cart’s products</li><li><strong>How</strong> to compute a cart’s product? By applying a discount (among other things)</li><li><strong>How</strong> to apply a discount? By performing arithmetic operations</li><li>and so on.</li></ul>

<img src=${callStackWhatHow} alt="call-stack-what-how" />

<p>It is common to describe the code at the bottom of the above image as “low-level” code (CPU, arithmetic, etc.) , and the code at the top as “high-level” code.</p>

<p>The “level” we’re talking about here is <strong>level of abstraction</strong>. And there are not just two levels, “low” and “high”. It’s rather a continuum where each levels is more or less abstract than the one next to it:</p>

<img src=${callStackLevelsOfAbstraction} alt="call-stack-levels-of-abstraction" />

<h2 id="Respecting-levels-of-abstraction">Respecting levels of abstraction</h2>

<p>Now that you’re familiar with the notion of levels of abstraction, how does that help in code?</p>

<p>One way to reduce coupling and make code more expressive is to <strong>respect</strong> levels of abstractions, which means that each part of the code (function, class, file, bigger component, …) should have one consistent abstraction level.</p>

<p>To illustrate, let’s consider the algorithm to compute a price in our e-commerce application from above. Let’s say that each product has a margin (e.g. +5%), then we apply the discount if the product happens to have one.</p>

<p>Discounts can be either relative (e.g. -10%) or absolute (e.g. -10$).</p>

<p>Let’s consider a slightly different implementation of <code>price_product</code> than the one from the call stack, one that doesn’t call an <code>apply_discount</code> function:</p>

<pre><code class="language-ruby">def price_product(product)
  selling_price = product.purchase_price * (1 + product.margin)
  return selling_price unless product.discount
  discounted_price = case product.discount_type
  when :relative then selling_price * (1 - product.discount_value)
  when :absolute then selling_price - product.discount_value
  end
  discounted_price
end</code></pre>

<p>This code can be improved by better respecting levels of abstraction. If we go back to how to compute a price, we have to:</p>

<ul><li>apply a margin</li><li>cap the price to a maximum</li><li>apply a potential discount</li></ul>

<p>But the code doesn’t reflect this. Instead of showing that a discount is applied, it shows the details of <strong>How</strong> to apply a discount. Our function then has a mix of levels of abstraction:</p>

<ul><li><strong>How </strong>to compute a price (the correct level, in blue below)</li><li><strong>How</strong> to apply a discount (a too low level, in red below)</li></ul>

<img src=${snippetAbstractions} alt="snippet-abstractions" />

<h3 id="Introducing-a-missing-level-of-abstraction">Introducing a missing level of abstraction</h3>

<p>To have all the code of <code>price_product</code> at the level of abstraction of <strong>How</strong> to price a product, we can introduce a level of abstraction, <code>apply_discount</code>, that hides the code in red:</p>

<img src=${snippetAbstractionsFix} alt="snippet-abstraction-fix" />

<p>There are two main benefits of respecting levels of abstraction.</p>

<p>First, it <strong>reduces coupling</strong>: if we need to change the algorithm of discount, we no longer need to modify <code>price_product</code>. Those two pieces of code can even live in separate files.</p>

<p>Second, it <strong>makes code more expressive</strong>, as you don’t have to understand the code in red to know what it’s doing, it now has a label on it: <code>apply_discount</code>. It’s as in the <a href="https://github.com/jnguyen095/clean-code/issues/28">newspaper metaphor</a>: when you read a well-written article, you can read only the beginning to know what it’s about, and choose to continue reading if you want the details.</p>

<h3 id="Abstraction-in-data-representation">Abstraction in data representation</h3>

<p>So far we’ve seen levels of abstraction in functions, but the same principle holds for data structure.</p>

<p>Our product contains the following attributes:</p>

<ul><li><code>purchase_price</code></li><li><code>desired_margin</code></li><li><code>category</code></li><li><code>discount_type</code></li><li><code>discount_value</code></li></ul>

<p><code>purchase_price</code>, <code>desired_margin</code> and <code>category</code> are used to compute the price, but <code>discount_type</code> and <code>discount_product</code> are used to compute the discount in the price, which is at a lower level of abstraction than the total price, as we saw.</p>

<p>One way to reflect this in the data structure of the product is to introduce a <code>discount</code> attribute, that hides the data related to the computation of discount. The product will then contain:</p>

<ul><li><code>purchase_price</code></li><li><code>desired_margin</code></li><li><code>category</code></li><li><code>discount</code></li></ul>

<p>And the <code>discount</code> contains:</p>

<ul><li><code>type</code></li><li><code>value</code></li></ul>

<p>Here is the code with this new data representation:</p>

<pre><code class="language-ruby">def apply_discount(selling_price, discount)
  return selling_price unless discount
  discounted_price = case discount.type
  when :relative then selling_price * (1 - discount.value)
  when :absolute then selling_price - discount.value
  end
  discounted_price
end

def price_product(product)
  selling_price = product.purchase_price * (1 + product.margin)
  apply_discount(selling_price, product.discount)
end</code></pre>

<p>Such an introduction of levels of abstraction with data had the same kinds of benefits as previously:</p>

<p>First, it <strong>reduced coupling</strong>: if we add to or change the attributes of the discount, we don’t need to change the attributes of the product, nor the interface of <code>apply_discount</code> nor the code of <code>price_product</code>.</p>

<p>Second, it makes the code <strong>more expressive</strong>: on line 14 in the above snippet there is only one parameter about discount that we need to understand, and <code>apply_discount</code> takes less parameters.</p>

<h2 id="What-could-go-wrong-with-abstractions">What could go wrong with abstractions</h2>

<p>Introducing abstractions in the above examples works well because it creates a code with clear and consistent levels of abstractions.</p>

<p>But introducing <em>any</em> abstraction is not always beneficial. There are three common types of abstractions that do the exact opposite of those above:</p>

<ul><li>leaky abstractions: there is coupling between the levels of abstraction, requiring to update one when we change the other</li><li>bureaucratic abstractions: the new abstraction is at the same level as the code it replaces</li><li>premature abstractions: the code containing the new abstraction is less expressive, we need to read the lower level code to understand the higher level code.</li></ul>

<p>Here are more details on those <a href="/encapsulation">abstractions anti-patterns</a>.</p>

<p>Every time you introduce a level of abstraction, make sure that it has the two expected benefits of reducing coupling and making code more expressive.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>A level of abstraction is defined by <strong>What</strong> the code does</li><li>The level of abstraction below it expresses <strong>How</strong> to do that</li><li>Each part of the code should have one consistent level of abstraction</li><li>Respecting levels of abstraction reduces coupling and makes code expressive</li><li>Data also has levels of abstraction</li><li>Incorrectly used, abstraction can increase coupling and make code less expressive</li></ul>
</div>`

export default content
